import React from 'react';
import { Stack } from '@mui/material';
import { Company, CompanyAdminMeta } from '@dispatch/shared/types';
import { CompanyGroupAttributes } from '@dispatch/shared/types';
import { AttributeAdmin } from '../components/AttributeAdmin';
import { ResourceRoleAdmin } from '../components/ResourceRoleAdmin';
import { TeamAdmin } from '../components/TeamAdmin';
import { OfficeAdmin } from '../components/OfficeAdmin';
import { CertAndLicenseAdmin } from '../components/CertAndLicenseAdmin';
import { CompanySettingsAdmin } from '../components/CompanySettingsAdmin';
import { GroupAdmin } from '../components/GroupAdmin';
import { GroupAttributeAdmin } from '../components/GroupAttributeAdmin';

export const CompanyAdminContext = React.createContext<{
  open: boolean;
  setOpen: React.Dispatch<boolean> | undefined;
}>({
  open: false,
  setOpen: undefined,
});

export const CompanyAdminDetails = ({
  company,
  groupAttributes,
  modalState,
  isLoading,
  handleCompanySettingsSave,
  companyUpsertStatus,
}: {
  company?: Company;
  groupAttributes?: CompanyGroupAttributes[]; // Use proper type
  modalState: {
    meta: CompanyAdminMeta;
    setmeta: React.Dispatch<CompanyAdminMeta>;
  };
  isLoading?: boolean;
  handleCompanySettingsSave: (data: Record<string, string>) => void;
  companyUpsertStatus: 'success' | 'error' | 'loading' | 'idle';
}) => {
  const attributes = React.useMemo(() => {
    return company?.attributeDefinition;
  }, [company?.attributeDefinition]);

  const memoizedGroupAttributes = React.useMemo(() => {
    return groupAttributes || [];
  }, [groupAttributes]);
  const resourceRoles = React.useMemo(() => {
    return company?.role || [];
  }, [company?.role]);
  const teams = React.useMemo(() => {
    return company?.team;
  }, [company?.team]);
  const groups = React.useMemo(() => {
    return company?.group;
  }, [company?.group]);
  const offices = React.useMemo(() => {
    return company?.office;
  }, [company?.office]);
  const licensesAndCertificates = React.useMemo(() => {
    return company?.certificationAndLicense;
  }, [company?.certificationAndLicense]);

  return (
    <Stack sx={{ gap: '20px' }}>
      <CompanySettingsAdmin
        company={company}
        isLoading={isLoading}
        companyUpsertStatus={companyUpsertStatus}
        handleCompanySettingsSave={handleCompanySettingsSave}
      />
      <AttributeAdmin
        attributes={attributes}
        modalState={modalState}
        isLoading={isLoading}
      />
      <GroupAttributeAdmin
        attributes={memoizedGroupAttributes}
        modalState={modalState}
        isLoading={isLoading}
      />

      <ResourceRoleAdmin
        resourceRoles={resourceRoles}
        modalState={modalState}
        isLoading={isLoading}
      />
      <TeamAdmin teams={teams} modalState={modalState} isLoading={isLoading} />
      <GroupAdmin
        groups={groups}
        modalState={modalState}
        isLoading={isLoading}
      />
      <OfficeAdmin
        offices={offices}
        modalState={modalState}
        isLoading={isLoading}
      />
      <CertAndLicenseAdmin
        certsAndLicense={licensesAndCertificates}
        modalState={modalState}
        isLoading={isLoading}
      />
    </Stack>
  );
};
